import LoadingSpinner from '../../../../udg_master_sitepackage/Resources/Private/JavaScript/helper/loading-spinner';
import AlgoliaUtils from './algoliaUtils';

/* eslint no-underscore-dangle: ['error', { 'allow': ['_quickResultsDebounceTimer', '_quickResultsAbortController'] }] */

class AlgoliaAjaxActions {
    constructor() {
        this.loadingSpinner = new LoadingSpinner();
        this.hitsPerPage = 10;

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', AlgoliaAjaxActions.init.bind(this), false);
        } else {
            AlgoliaAjaxActions.init();
        }
    }

    static init() {
    }

    // Combines the autosuggest and per-facet top-results queries into a single debounced
    // request, since both are always needed together for the same keystroke (see caller).
    async performAjaxQuickResults(debounceDelay = 300) {
        const self = this;

        clearTimeout(this._quickResultsDebounceTimer);
        // cancel a still in-flight request so its (possibly slower) response can never overwrite a newer one
        if (this._quickResultsAbortController) {
            this._quickResultsAbortController.abort();
        }

        return new Promise((resolve, reject) => {
            this._quickResultsDebounceTimer = setTimeout(async () => {
                const abortController = new AbortController();
                self._quickResultsAbortController = abortController;

                const params = await AlgoliaUtils.getCurrentSearchParams();
                const { q, facets } = params;

                const queryParams = new URLSearchParams({
                    type: 1706000001,
                    'tx_msqdxalgolia_searchdetail[action]': 'algoliaQuickResults',
                    'tx_msqdxalgolia_searchdetail[controller]': 'Search',
                    'tx_msqdxalgolia_searchdetail[query]': q,
                    'tx_msqdxalgolia_searchdetail[hitsPerPage]': this.hitsPerPage ? this.hitsPerPage : 10,
                });

                // URLSearchParams would flatten the array into a single comma separated value
                facets.forEach((facet) => queryParams.append('tx_msqdxalgolia_searchdetail[facetFilters][]', facet));

                const url = AlgoliaUtils.getAjaxEndpoint();
                const separator = url.includes('?') ? '&' : '?';

                try {
                    self.loadingSpinner.add('.js-gs-suggestions--best-queries');
                    self.loadingSpinner.add('.js-gs-suggestions--best-results');
                    const response = await fetch(`${url}${separator}${queryParams.toString()}`, {
                        method: 'GET',
                        signal: abortController.signal,
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error: ${response.status}`);
                    }

                    self.loadingSpinner.remove();
                    resolve(response.json());
                } catch (error) {
                    self.loadingSpinner.remove();
                    if (error.name === 'AbortError') {
                        resolve(undefined);
                        return;
                    }
                    reject(error);
                }
            }, debounceDelay);
        });
    }

    // perform the request to get the suggestions
    async performBestHits() {
        const self = this;
        const params = await AlgoliaUtils.getCurrentSearchParams();
        const { q } = params;

        self.loadingSpinner.add('.js-gs-suggestions--best-queries');
        const response = await this.searchClient.search({
            requests: [
                { indexName: 'oneforall', query: q, hitsPerPage: 25 },
            ],
        });
        return response;
    }

    // perform the search via the PHP AJAX endpoint (algoliaAction)
    // isRetryAfterFacetReset guards against infinite recursion once the type facet was reset to "all"
    async performAjaxSearch(page, isRetryAfterFacetReset = false) {
        const self = this;
        const params = await AlgoliaUtils.getCurrentSearchParams();
        const { q, facets } = params;

        // show loading spinner while request
        if (document.querySelector('.js-searchresults-list')) {
            AlgoliaUtils.hideSearchResultTiles();
            self.loadingSpinner.add('.js-searchresults-list');
        }

        // await AlgoliaUtils.checkForCurrentPageOnlyFilter();
        await AlgoliaUtils.updateLocationParams(q, facets);
        await AlgoliaUtils.updateSearchInputValues();

        const queryParams = new URLSearchParams({
            type: 1706000001,
            'tx_msqdxalgolia_searchdetail[action]': 'algoliaSearch',
            'tx_msqdxalgolia_searchdetail[controller]': 'Search',
            'tx_msqdxalgolia_searchdetail[query]': q,
            'tx_msqdxalgolia_searchdetail[page]': page,
            'tx_msqdxalgolia_searchdetail[hitsPerPage]': this.hitsPerPage ? this.hitsPerPage : 10,
        });

        // URLSearchParams would flatten the array into a single comma separated value
        facets.forEach((facet) => queryParams.append('tx_msqdxalgolia_searchdetail[facetFilters][]', facet));

        const url = AlgoliaUtils.getAjaxEndpoint();
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}${queryParams.toString()}`, {
            method: 'GET',
        });

        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
        }

        if (document.querySelector('.js-searchresults-list')) {
            self.loadingSpinner.remove();
        }

        const json = await response.json();

        // a query change can leave a previously selected type facet (product/page/document)
        // without any results - fall back to "all" and redo the search once
        const hasTypeFacet = facets.some((facet) => facet.startsWith('type:'));
        const nbHits = Number(json.results[0].nbHits) || 0;

        if (!isRetryAfterFacetReset && hasTypeFacet && nbHits === 0) {
            AlgoliaUtils.resetFacetToAll();
            return this.performAjaxSearch(page, true);
        }

        return json;
    }
}

export default new AlgoliaAjaxActions();
